import { useEffect, useState } from "react";

/**
 * Tiny useMediaQuery hook — subscribes to a matchMedia query and
 * re-renders when its match state flips. Safe against SSR (returns
 * false until mounted).
 *
 * Usage:
 *   const isMobile = useMediaQuery("(max-width: 639px)");
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // Newer browsers use addEventListener; fall back for very old ones.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
    } else {
      mql.addListener(onChange);
    }
    // Sync initial state in case media changed between render + effect.
    setMatches(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
}
